import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { AccessRightType, ApplicationSettingsType, FiImageSize, ObjectTypes } from '@fi-shared/types';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { ITask } from '@fi-shared/fi-task-shared/fi-task-shared';
import { isEqual } from '@fi-shared/utils';
import { AccessRightFilterCollectionModel, IAccessRightFilterCollection } from '@fi-shared/models';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { takeUntil } from 'rxjs/operators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { AppTextService } from '@fi-shared/app-text';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { Sort } from '@angular/material/sort';
import {
    IChecklist2ForReport,
    IChecklist2ReportAnswer,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { FiChecklist2ReportDeviationTaskType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { merge } from 'rxjs';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';

@Component({
    selector: 'fi-deviation-checklist-details-table',
    templateUrl: './deviation-checklist-details-table.component.html',
    styleUrls: ['./deviation-checklist-details-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-deviation-checklist-details-table' },
    standalone: false,
})
export class FiDeviationChecklistDetailsTable
    extends FiInfiniteScrollDataSource<IChecklist2ReportAnswer>
    implements OnInit, OnDestroy
{
    @Input() deviationItems: IChecklist2ReportAnswer[] = [];
    @Input() isDeviationBadgeShown: boolean;
    @Input() newDeviationAnswerIds: number[] = [];
    @Input() availableChecklists: IChecklist2ForReport[] = [];
    @Input() isChecklistNameShown: boolean;
    @Input() isCheckpointNameShown: boolean;
    @Input() isScoringShown: boolean;

    @Output() onTableSort = new EventEmitter<void>();
    @Output() onOpenConversation = new EventEmitter<void>();
    defaultSortColumnName: string = 'BusinessUnitName';
    itemsListType = FiItemsListType.ChecklistReportOpenChecklistsDeviationsTable;
    objectType: ObjectTypes = ObjectTypes.Checklist2;
    listItemActions: IItemsListAction2[] = [];
    private isTaskV2: boolean;

    constructor(
        injector: Injector,
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private fiTaskSharedService: FiTaskSharedService,
        private appTextService: AppTextService,
        private chatService: FiChatService,
        private fiUserDataService: FiUserDataService,
        private fiChecklist2ReportDialogsService: FiChecklist2ReportDialogsService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private fiTaskV2Service: FiTaskV2Service,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiLoading: LoadingService,
        private saveChangeWarningService: SaveChangesWarningService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.setListItemActions();
        this.subscribeOnCheckpointActionsChange();
        this.isTaskV2 = this.fiTaskV2HelperService.getNormalizedTaskV2();
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.deviationItems;
        if (!!change && !isEqual(change.currentValue, change.previousValue)) this.reloadData();
    }

    private setListItemActions() {
        this.listItemActions = [
            {
                title: this.appTextService.get('checklist.see_automated_action'),
                onClick: (item: IChecklist2ReportAnswer) => {
                    this.showDeviationTasksDialog(item, FiChecklist2ReportDeviationTaskType.AutomatedTask);
                },
                iconClass: 'fas fa-eye',
                optionType: FiItemsListOptionType.Icon,
                condition: (item: IChecklist2ReportAnswer) =>
                    this.hasAccessToAutomatedTasks(item) && this.isAutomatedTaskAdded(item),
            },
            {
                title: this.appTextService.get('task.create_automated_action'),
                onClick: (item: IChecklist2ReportAnswer) => {
                    this.showDeviationTasksDialog(item, FiChecklist2ReportDeviationTaskType.AutomatedTask);
                },
                iconClass: 'fas fa-list-check BrandedColorHover',
                optionType: FiItemsListOptionType.Icon,
                condition: (item: IChecklist2ReportAnswer) => {
                    return (
                        this.hasAccessToAutomatedTasks(item) &&
                        this.canCreateAutomatedTask(item) &&
                        !this.isAutomatedTaskAdded(item)
                    );
                },
            },
            {
                title: this.appTextService.get('checklist.see_one_time_task'),
                onClick: (item: IChecklist2ReportAnswer) => {
                    this.showDeviationTasksDialog(item, FiChecklist2ReportDeviationTaskType.OneTimeTask);
                },
                iconClass: 'fas fa-eye',
                optionType: FiItemsListOptionType.Icon,
                condition: (item: IChecklist2ReportAnswer) =>
                    this.hasAccessToOneTimeTasks() && this.isOneTimeTaskAdded(item),
            },
            {
                title: this.appTextService.get('task.create_one_time_task'),
                onClick: (item: IChecklist2ReportAnswer) => this.openAddOneTimeTaskDialog(item),
                iconClass: 'fas fa-check BrandedColorHover',
                optionType: FiItemsListOptionType.Icon,
                condition: (item: IChecklist2ReportAnswer) => {
                    return (
                        this.hasAccessToOneTimeTasks() &&
                        this.canCreateOneTimeTask(item) &&
                        !this.isOneTimeTaskAdded(item)
                    );
                },
            },
        ];

        this.listItemActions.push({
            title: this.appTextService.get('checklist.deviation_chat_reporter'),
            onClick: (item: IChecklist2ReportAnswer) => this.openChat(item.ChangedByProfile.Id),
            iconClass: 'fal fa-comment BrandedColorHover',
            optionType: FiItemsListOptionType.Icon,
            condition: (item: IChecklist2ReportAnswer) => !this.isCurrentUserReported(item.ChangedByProfile.Id),
        });
    }

    private isUserHasEditAccessToChecklist(checklistId: number): boolean {
        return this.availableChecklists.find((checklist: IChecklist2ForReport) => {
            return checklist.Id === checklistId;
        }).HasEditAccess;
    }

    private isCurrentUserReported(userId: number): boolean {
        return this.fiUserDataService.getUserId() === userId;
    }

    private openChat(userId: number) {
        this.chatService.triggerOpenOneToOneConversationByUserId(userId);
        this.onOpenConversation.emit();
    }

    isAutomatedTaskAdded(item: IChecklist2ReportAnswer): boolean {
        return this.fiChecklist2ReportHelperService.isAutomatedActionAdded(item);
    }

    isOneTimeTaskAdded(item: IChecklist2ReportAnswer): boolean {
        return this.fiChecklist2ReportHelperService.isOneTimeTaskAdded(item);
    }

    private hasAccessToTasks(isTaskV2: boolean): boolean {
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            isTaskV2 ? ApplicationSettingsType.TaskV2Local : ApplicationSettingsType.Task,
            AccessRightType.Read,
        );
    }

    private _taskAccessRightFilterCollection: IAccessRightFilterCollection;
    private getTaskAccessRightFilterCollection(
        isTaskV2: boolean,
        taskType: FiChecklist2ReportDeviationTaskType,
    ): Promise<IAccessRightFilterCollection> {
        if (taskType === FiChecklist2ReportDeviationTaskType.AutomatedTask) {
            // we should fetch access rights for checklists v2 module
            return this.fiTaskV2Service.getTaskAccessRightFilterCollection(FiTaskType.ChecklistAutomated, isTaskV2);
        }

        const accessRightsPromise = new Promise<IAccessRightFilterCollection>((resolve, reject) => {
            if (this._taskAccessRightFilterCollection) {
                // task access rights already loaded, use them
                resolve(this._taskAccessRightFilterCollection);
            } else if (this.hasAccessToTasks(isTaskV2)) {
                return this.fiTaskV2Service
                    .getTaskAccessRightFilterCollection(FiTaskType.ChecklistOneTime, isTaskV2)
                    .then((result: IAccessRightFilterCollection) => {
                        // user has access to tasks, load access rights and cache them
                        this._taskAccessRightFilterCollection = result;
                        resolve(this._taskAccessRightFilterCollection);
                    }, reject);
            } else {
                // user has no access to tasks module, use an empty access rights collection
                this._taskAccessRightFilterCollection = AccessRightFilterCollectionModel.getNewInstanceWithItems();
                resolve(this._taskAccessRightFilterCollection);
            }
        });

        return this.fiLoading.trackPromise(accessRightsPromise);
    }

    protected canEditDeleteAutomatedTask(answerDetailsReport: IChecklist2ReportAnswer) {
        return this.isUserHasEditAccessToChecklist(answerDetailsReport.ChecklistId);
    }

    protected canEditDeleteOneTimeTask(answerDetailsReport: IChecklist2ReportAnswer, task: ITaskV2) {
        return this.fiTaskV2HelperService.isUserTaskCreator(task.CreatedByUserId);
        //return this.isUserHasEditAccessToChecklist(answerDetailsReport.ChecklistId); todo old logic
    }

    protected canCreateAutomatedTask(answerDetailsReport: IChecklist2ReportAnswer) {
        // Users do not need specific Task rights to create automated tasks from the checklist editor or report,
        // as these automated tasks does not have a specific creator in our task update.
        // also user should have edit access to checklist
        return this.isUserHasEditAccessToChecklist(answerDetailsReport.ChecklistId);
    }

    protected canCreateOneTimeTask(answerDetailsReport: IChecklist2ReportAnswer) {
        // The user does not need administrate/editing rights for the checklist.
        return this.hasAccessToTasks(this.isTaskV2);
        //return this.isUserHasEditAccessToChecklist(answerDetailsReport.ChecklistId); todo old logic
    }

    protected hasAccessToAutomatedTasks(answerDetailsReport: IChecklist2ReportAnswer): boolean {
        // Users do not need specific Task rights to see automated tasks from the checklist editor or report,
        /*// as these automated tasks does not have a specific creator in our task update.
        // also user should have edit access to checklist
        return this.isUserHasEditAccessToChecklist(answerDetailsReport.ChecklistId);*/
        return true;
        //return this.hasAccessToTasks(this.isTaskV2); todo old logic
    }

    protected hasAccessToOneTimeTasks(): boolean {
        return true;
        //return this.hasAccessToTasks(this.isTaskV2);
    }

    private showDeviationTasksDialog(
        answerDetailsReport: IChecklist2ReportAnswer,
        taskType: FiChecklist2ReportDeviationTaskType,
    ) {
        this.getTaskAccessRightFilterCollection(this.isTaskV2, taskType).then((accessRightFilterCollection) => {
            this.fiChecklist2ReportDialogsService.openDeviationTasksDialog({
                answerDetailsReport,
                taskType,
                accessRightFilterCollection: accessRightFilterCollection,
                canCreateAutomatedTask: this.canCreateAutomatedTask(answerDetailsReport),
                canCreateOneTimeTask: this.canCreateOneTimeTask(answerDetailsReport),
                canEditDeleteAutomatedTaskFn: () => this.canEditDeleteAutomatedTask(answerDetailsReport),
                canEditDeleteOneTimeTaskFn: (task) => this.canEditDeleteOneTimeTask(answerDetailsReport, task),
                hasAccessToAutomatedTasks: this.hasAccessToAutomatedTasks(answerDetailsReport),
                hasAccessToOneTimeTasks: this.hasAccessToOneTimeTasks(),
                isTaskV2: this.isTaskV2,
            });
        });
    }

    private openAddOneTimeTaskDialog(item: IChecklist2ReportAnswer) {
        this.getTaskAccessRightFilterCollection(this.isTaskV2, FiChecklist2ReportDeviationTaskType.OneTimeTask).then(
            (accessRightFilterCollection) => {
                this.fiChecklist2ReportDialogsService
                    .showDeviationOneTimeTaskDialog(
                        item.AnswerId,
                        accessRightFilterCollection,
                        this.isTaskV2,
                        undefined,
                        item.ChangedByProfile.Id,
                    )
                    .onYesButtonClick((oneTimeTask: ITask) => {
                        this.fiTaskSharedService.saveTask(oneTimeTask, this.isTaskV2).then((taskId) => {
                            oneTimeTask.Id = taskId;
                            item.OneTimeTaskIds.push(oneTimeTask.Id);
                            // now we should update OneTimeTasks in other components
                            this.fiChecklist2ReportHelperService.triggerOneTimeTasksChanged(item);
                        });
                    })
                    .afterClosed(() => (this.saveChangeWarningService.closeDialogOnTransition = false));
                this.saveChangeWarningService.closeDialogOnTransition = true;
            },
        );
    }

    isNewDeviation(answerId: number): boolean {
        return this.newDeviationAnswerIds.includes(answerId);
    }

    private subscribeOnCheckpointActionsChange() {
        merge(
            this.fiChecklist2ReportHelperService.checkpointActionsChanged$,
            this.fiChecklist2ReportHelperService.oneTimeTasksChanged$,
        )
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.cdr.markForCheck();
            });
    }

    getListData(): Promise<IChecklist2ReportAnswer[]> {
        return Promise.resolve(this.deviationItems);
    }

    tableSort($event: Sort) {
        this.onSortChanged($event);
        this.onTableSort.emit();
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiImageSize = FiImageSize;
}
